import React from 'react';
import {useParams} from 'react-router';

import {Card, CardContent} from '@design-system';

import useKeyDuties from '../Queries/useKeyDuties';
import {useOnboarding} from '../Onboarding/useOnboarding';
import ShareButton from '../Shared/ShareResource/ShareButton';
import {findByRouteKey, useCanonicalRouteParams} from '../Util/routeIdentifier';
import {ResourceNotice, ResourceQueryState} from './LoadingCard';
import {RESOURCE_TITLE_CLASSES, ResourceBreadcrumbs, ResourceExternalLink} from './ProCategories';
import {KeyDutyObligation} from './ProKeyDutiesStage';

const ProViewKeyDuty = () => {
  const {data: keyDuties, error, refetch} = useKeyDuties();
  const {onboardingState, fetchStages} = useOnboarding();
  const {stage: stageKey, duty: dutyKey} = useParams();

  // Each found by its slug, or by the numeric id of an address from before COM-478. A duty
  // of one stage is shown under that stage, whichever stage the address names; a duty of
  // every stage (no `stage_id`) under the stage the address names. One replace puts both
  // slugs in the address, once both records are found.
  const keyDuty = findByRouteKey(keyDuties, dutyKey);
  const stages = onboardingState?.stages;
  const stage =
    typeof keyDuty?.stage_id === 'number'
      ? stages?.find((candidate) => candidate.id === keyDuty.stage_id)
      : findByRouteKey(stages, stageKey);
  useCanonicalRouteParams({stage: stage?.slug, duty: keyDuty?.slug}, {resource: 'key-duty'});

  // While the stages load the duty is shown without its stage (COM-145). A stages read that
  // failed shows its failure with Retry in the duty's place, and a duty whose own stage is
  // not among the stages (one removed since) has no stage to be shown under, as that stage's
  // page has no duties: neither is shown as the duty at an address the page cannot place.
  const stagesFailed = stages === undefined && Boolean(onboardingState?.stagesFailed);
  const ownStageMissing = typeof keyDuty?.stage_id === 'number' && stages !== undefined && !stage;
  const shown = stagesFailed || ownStageMissing ? undefined : keyDuty;

  // A key duty carries up to three links as link1_title/link1_url … link3_*.
  const links = shown
    ? [1, 2, 3]
        .map((i) => ({title: shown[`link${i}_title`], url: shown[`link${i}_url`]}))
        .filter((link) => link.title && link.url)
    : [];

  return (
    <div data-testid="pro-view-key-duty">
      <ResourceBreadcrumbs
        items={[
          {label: 'Home', to: '/dashboard'},
          {label: 'Key Duties', to: '/key-duties'},
          // COM-145: without a resolved stage there is no trustworthy link to it.
          ...(stage
            ? [
                {
                  label: stage.medium_label,
                  to: `/key-duties/stage/${encodeURIComponent(stage.slug)}`,
                },
              ]
            : []),
          ...(shown ? [{label: shown.title}] : []),
        ]}
      />
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <h1 className={`flex-1 ${RESOURCE_TITLE_CLASSES}`}>{shown?.title ?? 'Key Duties'}</h1>
        {shown ? (
          <ShareButton
            resourceType="key_duty"
            resourceId={shown.id}
            resourceSlug={shown.slug}
            resourceName={shown.title}
          />
        ) : null}
      </div>
      <ResourceQueryState data={keyDuties} error={error} refetch={refetch} label="key duties">
        {!keyDuty ? (
          <ResourceNotice>Key duty not found.</ResourceNotice>
        ) : stagesFailed ? (
          <ResourceQueryState
            data={stages}
            error={onboardingState?.stagesFailed}
            refetch={fetchStages}
            label="transition stages"
          />
        ) : ownStageMissing ? (
          <ResourceNotice>Stage not found.</ResourceNotice>
        ) : (
          <Card>
            <CardContent className="p-6">
              <KeyDutyObligation obligation={shown.obligation} />
              <p className="m-0 mt-4 break-words">{shown.description}</p>
              {links.length ? (
                <ul className="m-0 mt-4 flex list-none flex-col gap-1 p-0">
                  {links.map((link, index) => (
                    <li key={`link-${index}`}>
                      <ResourceExternalLink href={link.url}>{link.title}</ResourceExternalLink>
                    </li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        )}
      </ResourceQueryState>
    </div>
  );
};

export default ProViewKeyDuty;
